import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { auth, calls as callsApi, hotels, shoppers as shoppersApi, surveys as surveysApi, users as usersApi } from "../api/endpoints";
import SearchableSelect from "../components/SearchableSelect";
import { readCallsReturnTo, withCallsReturnTo } from "../utils/callsNavigation";
import type { Call, CallHistory, CallStatus, CallSurveyInfo, HotelProperty, ServiceLevel, User } from "../types";

function scoreTone(score: number) {
  if (score >= 70) return "bg-emerald-100 text-emerald-800";
  if (score >= 50) return "bg-amber-100 text-amber-800";
  return "bg-rose-100 text-rose-800";
}

function statusTone(name: string) {
  const normalized = name.toLowerCase();
  if (normalized.includes("completed")) return "bg-emerald-100 text-emerald-800";
  if (normalized.includes("pending")) return "bg-amber-100 text-amber-800";
  if (normalized.includes("cancelled")) return "bg-rose-100 text-rose-800";
  if (normalized.includes("scheduled")) return "bg-sky-100 text-sky-800";
  if (normalized.includes("in progress")) return "bg-indigo-100 text-indigo-800";
  return "bg-slate-100 text-slate-700";
}

function externalUrl(url: string) {
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function collectRecipientEmails(fields: { label: string; email: string }[]) {
  const seen = new Set<string>();
  return fields
    .filter((field) => field.label.trim().toLowerCase() !== "from")
    .map((field) => field.email.trim())
    .filter((email) => {
      const key = email.toLowerCase();
      if (!email || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function serviceLevelLabel(level: ServiceLevel) {
  return [level.name, level.description].filter(Boolean).join(" - ");
}

interface ShopperOption { id: number; name: string; }
interface SalesStaffOption {
  id: number;
  name: string;
  email: string;
  business_phone: string;
}
const ADMIN_ROLES = ["SuperAdmin", "Admin"];
const REQUESTOR_ROLES = [
  "Client Account Executive",
  "Regional Representative",
  "Client Property Manager",
  "Director of Sales",
  "Director of Catering",
  "Client Sales Staff Individual",
];

export default function CallDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = readCallsReturnTo(location.state);

  const goBackToCalls = useCallback(() => {
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/calls");
  }, [navigate, returnTo]);
  const [call, setCall] = useState<Call | null>(null);
  const [history, setHistory] = useState<CallHistory[]>([]);
  const [surveyInfo, setSurveyInfo] = useState<CallSurveyInfo | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);

  // Email modals
  const [showReviewEmail, setShowReviewEmail] = useState(false);
  const [showStopEmail, setShowStopEmail] = useState(false);
  const [emailRecipientFields, setEmailRecipientFields] = useState<{ label: string; email: string }[]>([]);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailScore, setEmailScore] = useState<number | null>(null);
  const [emailReportUrl, setEmailReportUrl] = useState("");
  const [emailWaveUrl, setEmailWaveUrl] = useState("");
  const [emailTemplateSource, setEmailTemplateSource] = useState("");
  const [emailAttachments, setEmailAttachments] = useState<File[]>([]);

  // Editing state
  const [editing, setEditing] = useState(false);
  const [statuses, setStatuses] = useState<CallStatus[]>([]);
  const [serviceLevels, setServiceLevels] = useState<ServiceLevel[]>([]);
  const [shopperList, setShopperList] = useState<ShopperOption[]>([]);
  const [salesStaffList, setSalesStaffList] = useState<SalesStaffOption[]>([]);
  const [propertyList, setPropertyList] = useState<HotelProperty[]>([]);
  const [editFields, setEditFields] = useState({
    property_id: 0,
    status_id: 0,
    service_level_id: 0,
    sales_staff_id: null as number | null,
    shopper_id: null as number | null,
    sales_staff_name: "",
    scenario: "",
    notes: "",
    scheduled_call_date: "",
    earliest_call_date: "",
    property_contact_number: "",
    sales_staff_email: "",
    sales_staff_business_phone: "",
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    if (!id) return;
    const callId = Number(id);
    callsApi.get(callId).then((c) => {
      setCall(c);
      setEditFields({
        property_id: c.property_id,
        status_id: c.status.id,
        service_level_id: c.service_level.id,
        sales_staff_id: c.sales_staff_id || null,
        shopper_id: c.shopper_id || null,
        scenario: c.scenario || "",
        notes: c.notes || "",
        scheduled_call_date: c.scheduled_call_date || "",
        earliest_call_date: c.earliest_call_date || "",
        property_contact_number: c.property_contact_number || "",
        sales_staff_email: c.sales_staff_email || "",
        sales_staff_name: c.sales_staff_name || "",
        sales_staff_business_phone: c.sales_staff_business_phone || "",
      });
    });
    callsApi.history(callId).then(setHistory);
    surveysApi.forCall(callId).then(setSurveyInfo);
    callsApi.statuses().then(setStatuses);
    callsApi.serviceLevels().then(setServiceLevels);
    shoppersApi.list("limit=500").then((res) => {
      setShopperList(
        res.items
          .map((u: User) => ({ id: u.id, name: `${u.first_name} ${u.last_name}`.trim() }))
          .sort((a: ShopperOption, b: ShopperOption) => a.name.localeCompare(b.name))
      );
    });
    usersApi
      .salesStaff()
      .then((items) => setSalesStaffList(items))
      .catch(() => setSalesStaffList([]));
    auth.me().then(setCurrentUser).catch(() => null);
    hotels.properties
      .list("limit=5000")
      .then((res) => {
        setPropertyList(
          res.items.sort((a, b) =>
            `${a.name} ${a.hotel_code}`.localeCompare(`${b.name} ${b.hotel_code}`, undefined, { sensitivity: "base" })
          )
        );
      })
      .catch(() => setPropertyList([]));
  }, [id]);

  const openPdfReport = async () => {
    if (!id) return;
    setPdfLoading(true);
    setShowPdfModal(true);
    try {
      const blob = await surveysApi.generatePdf(Number(id));
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch {
      setPdfUrl(null);
    } finally {
      setPdfLoading(false);
    }
  };

  const closePdfModal = () => {
    setShowPdfModal(false);
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
  };

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  if (!call) {
    return (
      <div className="surface-panel px-6 py-10 text-center">
        <div className="eyebrow">Calls workspace</div>
        <p className="mt-3 text-lg text-slate-600">Loading call details...</p>
      </div>
    );
  }

  const displayCallId = call.legacy_id || call.id;
  const hasLegacyAlias = Boolean(call.legacy_id && call.legacy_id !== call.id);
  const hotelWebsiteUrl = externalUrl(call.property_website || "");
  const cityState = [call.property_city, call.property_state].filter(Boolean).join(", ");
  const propertyAddress = [call.property_address, [cityState, call.property_zip_code].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(" · ");

  const handleReset = async () => {
    if (!window.confirm("Reset this call back to its scheduled state? This will clear the survey, score, recording, and sent-email history.")) return;
    try {
      const updated = await callsApi.reset(call.id);
      setCall(updated);
      setToast({ type: "success", message: "Call reset successfully." });
      callsApi.history(call.id).then(setHistory);
    } catch {
      setToast({ type: "error", message: "Failed to reset call." });
    }
  };

  const handleUploadAudio = async () => {
    if (!audioFile) return;
    try {
      const updated = await callsApi.uploadAudio(call.id, audioFile);
      setCall(updated);
      setAudioFile(null);
      setToast({ type: "success", message: "Audio uploaded." });
    } catch {
      setToast({ type: "error", message: "Audio upload failed." });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Record<string, any> = {};
      const roleName = currentUser?.role?.name || "";
      const canEditProperty = ADMIN_ROLES.includes(roleName);
      if (canEditProperty && editFields.property_id !== call.property_id) payload.property_id = editFields.property_id;
      if (editFields.status_id !== call.status.id) payload.status_id = editFields.status_id;
      if (editFields.service_level_id !== call.service_level.id) payload.service_level_id = editFields.service_level_id;
      if (editFields.sales_staff_id !== (call.sales_staff_id || null)) payload.sales_staff_id = editFields.sales_staff_id;
      if (editFields.shopper_id !== (call.shopper_id || null)) payload.shopper_id = editFields.shopper_id;
      if (editFields.scenario !== (call.scenario || "")) payload.scenario = editFields.scenario;
      if (editFields.notes !== (call.notes || "")) payload.notes = editFields.notes;
      if (editFields.scheduled_call_date !== (call.scheduled_call_date || ""))
        payload.scheduled_call_date = editFields.scheduled_call_date || null;
      if (editFields.earliest_call_date !== (call.earliest_call_date || ""))
        payload.earliest_call_date = editFields.earliest_call_date || null;
      const isAssignedShopper = currentUser?.id === call.shopper_id;
      const canEditHotelPhone = ADMIN_ROLES.includes(roleName) || REQUESTOR_ROLES.includes(roleName) || isAssignedShopper;
      const canEditSalesContact = ADMIN_ROLES.includes(roleName) || currentUser?.id === call.sales_staff_id || isAssignedShopper;
      const hotelPhoneChanged = editFields.property_contact_number !== (call.property_contact_number || "");
      const salesNameChanged = editFields.sales_staff_name !== (call.sales_staff_name || "");
      const salesEmailChanged = editFields.sales_staff_email !== (call.sales_staff_email || "");
      const salesPhoneChanged = editFields.sales_staff_business_phone !== (call.sales_staff_business_phone || "");

      if (Object.keys(payload).length === 0 && !hotelPhoneChanged && !salesNameChanged && !salesEmailChanged && !salesPhoneChanged) {
        setEditing(false);
        return;
      }

      if (Object.keys(payload).length > 0) {
        await callsApi.update(call.id, payload);
      }
      if (
        (hotelPhoneChanged && canEditHotelPhone) ||
        ((salesNameChanged || salesEmailChanged || salesPhoneChanged) && canEditSalesContact)
      ) {
        await callsApi.updateContacts(call.id, {
          ...(hotelPhoneChanged && canEditHotelPhone
            ? { property_contact_number: editFields.property_contact_number }
            : {}),
          ...(salesEmailChanged && canEditSalesContact
            ? { sales_staff_email: editFields.sales_staff_email }
            : {}),
          ...(salesNameChanged && canEditSalesContact
            ? { sales_staff_name: editFields.sales_staff_name }
            : {}),
          ...(salesPhoneChanged && canEditSalesContact
            ? { sales_staff_business_phone: editFields.sales_staff_business_phone }
            : {}),
        });
      }
      const refreshed = await callsApi.get(call.id);
      setCall(refreshed);
      surveysApi.forCall(call.id).then(setSurveyInfo);
      setEditing(false);
      setToast({ type: "success", message: "Call updated." });
      callsApi.history(call.id).then(setHistory);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "Failed to update call.";
      setToast({ type: "error", message: typeof msg === "string" ? msg : JSON.stringify(msg) });
    } finally {
      setSaving(false);
    }
  };

  const openReviewEmailModal = async () => {
    if (!call) return;
    try {
      const preview = await surveysApi.emailPreview(call.id, "review");
      setEmailRecipientFields(preview.recipient_fields);
      setEmailSubject(preview.subject);
      setEmailBody(preview.body);
      setEmailScore(preview.score);
      setEmailReportUrl(preview.report_pdf_url);
      setEmailWaveUrl(preview.wave_file_url);
      setEmailTemplateSource(preview.template_source);
    } catch {
      setEmailRecipientFields([]);
      setEmailSubject(
        `${call.sales_staff_name || "Staff"} - ${call.property_name} - Call ID ${call.shop_id || call.id}`
      );
      setEmailBody("");
      setEmailScore(null);
      setEmailReportUrl("");
      setEmailWaveUrl("");
      setEmailTemplateSource("");
    }
    setEmailAttachments([]);
    setShowReviewEmail(true);
  };

  const handleSendReviewEmail = async () => {
    if (!call) return;
    setEmailSending(true);
    try {
      const recipients = collectRecipientEmails(emailRecipientFields);
      const payload = {
        call_id: call.id,
        recipients,
        subject: emailSubject,
        body: emailBody,
      };
      if (emailAttachments.length > 0) {
        await surveysApi.sendEmailWithAttachments(payload, emailAttachments);
      } else {
        await surveysApi.sendEmail(payload);
      }
      setShowReviewEmail(false);
      setToast({ type: "success", message: "Review email sent." });
      const updated = await callsApi.get(call.id);
      setCall(updated);
      callsApi.history(call.id).then(setHistory);
    } catch {
      setToast({ type: "error", message: "Failed to send review email." });
    } finally {
      setEmailSending(false);
    }
  };

  const openStopEmailModal = async () => {
    if (!call) return;
    try {
      const preview = await surveysApi.emailPreview(call.id, "stop");
      setEmailRecipientFields(preview.recipient_fields);
      setEmailSubject(preview.subject);
      setEmailBody(preview.body);
      setEmailScore(preview.score);
      setEmailReportUrl(preview.report_pdf_url);
      setEmailWaveUrl(preview.wave_file_url);
      setEmailTemplateSource(preview.template_source);
    } catch {
      setEmailRecipientFields([]);
      setEmailSubject(
        `${call.sales_staff_name || "Staff"} shop call STOP PROCESS notification`
      );
      setEmailBody("");
      setEmailScore(null);
      setEmailReportUrl("");
      setEmailWaveUrl("");
      setEmailTemplateSource("");
    }
    setShowStopEmail(true);
  };

  const handleSendStopEmail = async () => {
    if (!call) return;
    setEmailSending(true);
    try {
      const recipients = collectRecipientEmails(emailRecipientFields);
      await surveysApi.sendTerminationEmail({
        call_id: call.id,
        recipients,
        subject: emailSubject,
        body: emailBody,
      });
      setShowStopEmail(false);
      setToast({ type: "success", message: "Stop process email sent." });
      const updated = await callsApi.get(call.id);
      setCall(updated);
      callsApi.history(call.id).then(setHistory);
    } catch {
      setToast({ type: "error", message: "Failed to send stop process email." });
    } finally {
      setEmailSending(false);
    }
  };

  const responseCompleted = Boolean(surveyInfo?.response_is_completed);
  const surveyStarted = Boolean(surveyInfo?.has_existing_response && !responseCompleted);
  const surveyReady = Boolean(surveyInfo?.survey_id && !responseCompleted);
  const surveyDone = responseCompleted;
  const playableAudioUrl = call.audio_url || call.wave_file;
  const roleName = currentUser?.role?.name || "";
  const isAssignedShopper = currentUser?.id === call.shopper_id;
  const canEditProperty = ADMIN_ROLES.includes(roleName);
  const canEditHotelPhone = ADMIN_ROLES.includes(roleName) || REQUESTOR_ROLES.includes(roleName) || isAssignedShopper;
  const canEditSalesContact = ADMIN_ROLES.includes(roleName) || currentUser?.id === call.sales_staff_id || isAssignedShopper;
  const readyToSend = surveyDone && Boolean(playableAudioUrl) && !call.survey_email_sent;
  const propertyOptions = propertyList.map((property) => ({
    value: property.id,
    label: [
      `${property.hotel_code} - ${property.name}`,
      [property.city, property.state].filter(Boolean).join(", "),
      property.management_company?.name || "",
    ].filter(Boolean).join(" | "),
  }));
  const salesStaffOptions = [
    { value: 0, label: "Unassigned" },
    ...salesStaffList.map((staff) => ({
      value: staff.id,
      label: [staff.name, staff.email].filter(Boolean).join(" - "),
    })),
  ];

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 surface-panel px-5 py-3 text-sm font-semibold shadow-lg ${
            toast.type === "success" ? "border-l-4 border-emerald-500 text-emerald-800" : "border-l-4 border-rose-500 text-rose-800"
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Hero header */}
      <section className="surface-hero rise-in px-6 py-7 md:px-8">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0 flex-1">
            <button onClick={goBackToCalls} className="button-ghost !px-4 !py-2 text-sm">
              Back to Calls
            </button>
            <div className="mt-5">
              <div className="eyebrow">Call dossier</div>
              <h1 className="page-title">Call #{displayCallId}</h1>
              {hasLegacyAlias && (
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  New system ID #{call.id}
                </p>
              )}
              <p className="page-copy">
                {call.property_name} ({call.property_hotel_code})
                {call.management_company_name && ` — ${call.management_company_name}`}
              </p>
              {propertyAddress && (
                <p className="mt-2 text-sm font-medium text-slate-600">{propertyAddress}</p>
              )}
            </div>
          </div>

          <div className="w-full xl:max-w-[48rem] xl:flex-none">
            <div className="flex flex-col gap-3 xl:items-end">
              <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                {surveyDone && (
                  <span className="badge-pill bg-emerald-100 text-emerald-800">Survey Done</span>
                )}
                {surveyStarted && (
                  <span className="badge-pill bg-indigo-100 text-indigo-800">Survey Started</span>
                )}
                {readyToSend && (
                  <span className="badge-pill bg-amber-100 text-amber-800">Ready to Send</span>
                )}
                {call.survey_email_sent && (
                  <span className="badge-pill bg-emerald-50 text-emerald-700">Review Sent</span>
                )}
                {call.termination_email_sent && (
                  <span className="badge-pill bg-rose-50 text-rose-700">Stop Sent</span>
                )}
              </div>

              <div className="flex flex-wrap gap-3 xl:justify-end">
                {hotelWebsiteUrl && (
                  <a
                    href={hotelWebsiteUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="button-ghost min-w-[10rem] text-center"
                  >
                    Open hotel website
                  </a>
                )}
                {surveyReady && (
                  <button
                    onClick={() => navigate(`/calls/${call.id}/survey`, { state: withCallsReturnTo(location.state, returnTo) })}
                    className="button-primary min-w-[10rem]"
                  >
                    {surveyStarted ? "Edit Survey" : "Fill Survey"}
                  </button>
                )}
                {surveyDone && (
                  <>
                    <button onClick={openPdfReport} className="button-accent min-w-[10rem]">
                      View Report
                    </button>
                    <button
                      onClick={() => navigate(`/calls/${call.id}/survey`, { state: withCallsReturnTo(location.state, returnTo) })}
                      className="button-primary min-w-[10rem]"
                    >
                      Edit Survey
                    </button>
                    <button
                      onClick={openReviewEmailModal}
                      className={`${call.survey_email_sent
                        ? "button-ghost border-emerald-500 text-emerald-700"
                        : "button-primary"} min-w-[10rem]`}
                    >
                      {call.survey_email_sent ? "\u2713 Review Sent" : "Email Review"}
                    </button>
                    <button
                      onClick={openStopEmailModal}
                      className={`${call.termination_email_sent
                        ? "button-ghost border-rose-500 text-rose-700"
                        : "button-ghost"} min-w-[10rem]`}
                    >
                      {call.termination_email_sent ? "\u2713 Stop Sent" : "Stop Process"}
                    </button>
                  </>
                )}
              </div>

              <div className="flex flex-wrap gap-3 xl:justify-end">
                {!editing ? (
                  <button onClick={() => setEditing(true)} className="button-accent min-w-[10rem]">
                    Edit Call
                  </button>
                ) : (
                  <>
                    <button onClick={handleSave} disabled={saving} className="button-primary min-w-[10rem]">
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                    <button onClick={() => setEditing(false)} className="button-ghost min-w-[10rem]">
                      Cancel
                    </button>
                  </>
                )}
                <button onClick={handleReset} className="button-ghost min-w-[10rem]">
                  Reset
                </button>
                <button
                  onClick={async () => {
                    if (window.confirm("Delete this shop from the active database? It will be archived and removed from active queues.")) {
                      await callsApi.archive(call.id);
                      goBackToCalls();
                    }
                  }}
                  className="button-danger min-w-[10rem]"
                >
                  Delete shop
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Survey resolution info bar */}
      {surveyInfo && (
        <section className="surface-panel rise-in p-4 md:px-6">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="label-caps">Survey</span>
            {surveyInfo.survey_name ? (
              <span className="badge-pill bg-teal-100 text-teal-800">{surveyInfo.survey_name}</span>
            ) : (
              <span className="badge-pill bg-amber-100 text-amber-800">No survey configured for this HMC</span>
            )}
            {surveyInfo.management_company_name && (
              <span className="text-slate-500">via {surveyInfo.management_company_name} contract</span>
            )}
            {surveyDone && <span className="badge-pill bg-emerald-100 text-emerald-800">Response submitted</span>}
            {surveyStarted && <span className="badge-pill bg-indigo-100 text-indigo-800">Response saved</span>}
          </div>
        </section>
      )}

      {/* KPI cards */}
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="kpi-card rise-in px-6 py-5">
          <div className="label-caps">Status</div>
          {editing ? (
            <select
              value={editFields.status_id}
              onChange={(e) => setEditFields({ ...editFields, status_id: Number(e.target.value) })}
              className="select-shell mt-2"
            >
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          ) : (
            <>
              <div className="mt-3 text-xl font-semibold tracking-tight">{call.status.name}</div>
              <div className="mt-3">
                <span className={`badge-pill ${statusTone(call.status.name)}`}>{call.call_type.name}</span>
              </div>
            </>
          )}
        </div>
        <div className="kpi-card rise-in delay-1 px-6 py-5">
          <div className="label-caps">Survey score</div>
          <div className="mt-3 text-xl font-semibold tracking-tight">
            {call.percent_achieved != null ? `${call.percent_achieved}%` : "Pending"}
          </div>
          {call.percent_achieved != null && (
            <div className="mt-4">
              <div className="progress-track">
                <div className="progress-fill" style={{ width: `${Math.min(call.percent_achieved, 100)}%` }} />
              </div>
            </div>
          )}
        </div>
        <div className="kpi-card rise-in delay-2 px-6 py-5">
          <div className="label-caps">Call cost</div>
          <div className="mt-3 text-xl font-semibold tracking-tight">${call.call_cost.toFixed(2)}</div>
          <div className="mt-3 text-sm text-slate-600">{call.is_payment_made ? "Payment marked" : "Awaiting payment"}</div>
        </div>
        <div className="kpi-card rise-in delay-3 px-6 py-5">
          <div className="label-caps">Scheduling</div>
          {editing ? (
            <div className="mt-2 space-y-2">
              <label className="text-xs text-slate-500">Scheduled</label>
              <input
                type="date"
                value={editFields.scheduled_call_date}
                onChange={(e) => setEditFields({ ...editFields, scheduled_call_date: e.target.value })}
                className="input-shell"
              />
              <label className="text-xs text-slate-500">Earliest</label>
              <input
                type="date"
                value={editFields.earliest_call_date}
                onChange={(e) => setEditFields({ ...editFields, earliest_call_date: e.target.value })}
                className="input-shell"
              />
            </div>
          ) : (
            <>
              <div className="mt-3 text-xl font-semibold tracking-tight">{call.scheduled_call_date || "Not set"}</div>
              <div className="mt-3 text-sm text-slate-600">Earliest: {call.earliest_call_date || "Not set"}</div>
            </>
          )}
        </div>
      </section>

      {/* Detail panels */}
      <section className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr),minmax(0,0.9fr)]">
        <div className="surface-panel rise-in delay-1 p-6 md:p-7">
          <div className="eyebrow">Operational detail</div>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight">Call details</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="surface-muted p-4 sm:col-span-2">
              <div className="label-caps">Hotel property</div>
              {editing && canEditProperty ? (
                <div className="mt-2">
                  <SearchableSelect
                    options={propertyOptions}
                    value={editFields.property_id}
                    onChange={(value) => setEditFields({ ...editFields, property_id: Number(value) })}
                    placeholder="Select hotel property"
                    disabled={propertyOptions.length === 0}
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    Changing this updates only this shop call. It does not rename the hotel record.
                  </p>
                </div>
              ) : (
                <div className="mt-2">
                  <div className="text-lg font-semibold tracking-tight">{call.property_name} ({call.property_hotel_code})</div>
                  {propertyAddress ? <div className="mt-1 text-sm text-slate-600">{propertyAddress}</div> : null}
                </div>
              )}
            </div>
            <div className="surface-muted p-4">
              <div className="label-caps">Order</div>
              <div className="mt-2 text-lg font-semibold tracking-tight">
                {call.order_id ? `#${call.order_id}` : "Unlinked legacy call"}
              </div>
            </div>
            <div className="surface-muted p-4">
              <div className="label-caps">Shop ID</div>
              <div className="mt-2 text-lg font-semibold tracking-tight">{call.shop_id || "Not set"}</div>
            </div>
            <div className="surface-muted p-4">
              <div className="label-caps">Service level</div>
              {editing ? (
                <select
                  value={editFields.service_level_id}
                  onChange={(e) => setEditFields({ ...editFields, service_level_id: Number(e.target.value) })}
                  className="select-shell mt-2"
                >
                  {serviceLevels.map((level) => (
                    <option key={level.id} value={level.id}>{serviceLevelLabel(level)}</option>
                  ))}
                </select>
              ) : (
                <div className="mt-2 text-lg font-semibold tracking-tight">{serviceLevelLabel(call.service_level)}</div>
              )}
            </div>
            <div className="surface-muted p-4">
              <div className="label-caps">Contract year</div>
              <div className="mt-2 text-lg font-semibold tracking-tight">{call.contract_year || "Not set"}</div>
            </div>
            <div className="surface-muted p-4">
              <div className="label-caps">Hotel main line</div>
              {editing && canEditHotelPhone ? (
                <input
                  value={editFields.property_contact_number}
                  onChange={(e) => setEditFields({ ...editFields, property_contact_number: e.target.value })}
                  className="input-shell mt-2"
                />
              ) : (
                <div className="mt-2 text-lg font-semibold tracking-tight">{call.property_contact_number || "Not set"}</div>
              )}
            </div>
            <div className="surface-muted p-4">
              <div className="label-caps">Hotel website</div>
              {hotelWebsiteUrl ? (
                <a
                  href={hotelWebsiteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 block break-words text-lg font-semibold tracking-tight text-teal-700 underline-offset-4 hover:underline"
                >
                  {call.property_website}
                </a>
              ) : (
                <div className="mt-2 text-lg font-semibold tracking-tight">Not set</div>
              )}
            </div>
            <div className="surface-muted p-4">
              <div className="label-caps">Sales direct line</div>
              {editing && canEditSalesContact ? (
                <input
                  value={editFields.sales_staff_business_phone}
                  onChange={(e) => setEditFields({ ...editFields, sales_staff_business_phone: e.target.value })}
                  className="input-shell mt-2"
                />
              ) : (
                <div className="mt-2 text-lg font-semibold tracking-tight">{call.sales_staff_business_phone || "Not set"}</div>
              )}
            </div>
            <div className="surface-muted p-4">
              <div className="label-caps">Guest rooms</div>
              <div className="mt-2 text-lg font-semibold tracking-tight">{call.property_guest_rooms || 0}</div>
            </div>
            <div className="surface-muted p-4">
              <div className="label-caps">Meeting space</div>
              <div className="mt-2 text-lg font-semibold tracking-tight">
                {call.property_meeting_space_sqft ? `${call.property_meeting_space_sqft.toLocaleString()} sq ft` : "Not set"}
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <div>
              <div className="label-caps">Scenario</div>
              {editing ? (
                <textarea
                  value={editFields.scenario}
                  onChange={(e) => setEditFields({ ...editFields, scenario: e.target.value })}
                  className="textarea-shell mt-2 min-h-[100px]"
                  rows={4}
                />
              ) : (
                <div className="surface-muted mt-2 p-4 text-sm leading-6 text-slate-600">
                  {call.scenario || "No scenario notes entered."}
                </div>
              )}
            </div>
            <div>
              <div className="label-caps">Competition comments</div>
              <div className="surface-muted mt-2 p-4 text-sm leading-6 text-slate-600">
                {call.property_competition_comments || "No competition comments entered."}
              </div>
            </div>
            <div>
              <div className="label-caps">Notes</div>
              {editing ? (
                <textarea
                  value={editFields.notes}
                  onChange={(e) => setEditFields({ ...editFields, notes: e.target.value })}
                  className="textarea-shell mt-2 min-h-[100px]"
                  rows={4}
                />
              ) : (
                <div className="surface-muted mt-2 p-4 text-sm leading-6 text-slate-600">
                  {call.notes || "No call notes entered."}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="surface-panel rise-in delay-2 p-6 md:p-7">
            <div className="eyebrow">People and flags</div>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight">Participants</h2>
            <div className="mt-6 space-y-4 text-sm text-slate-700">
              <div className="flex items-center justify-between gap-4">
                <span>Sales staff</span>
                {editing ? (
                  <div className="w-full max-w-[16rem]">
                    <SearchableSelect
                      options={salesStaffOptions}
                      value={editFields.sales_staff_id || 0}
                      onChange={(value) => {
                        const nextId = Number(value) || null;
                        const selected = salesStaffList.find((staff) => staff.id === nextId);
                        setEditFields({
                          ...editFields,
                          sales_staff_id: nextId,
                          sales_staff_name: selected?.name || "",
                          sales_staff_email: selected?.email || "",
                          sales_staff_business_phone: selected?.business_phone || "",
                        });
                      }}
                      placeholder="Search sales staff"
                      disabled={salesStaffOptions.length <= 1}
                    />
                  </div>
                ) : (
                  <span className="font-semibold text-slate-900">{call.sales_staff_name || "Unassigned"}</span>
                )}
              </div>
              <div className="flex items-center justify-between gap-4">
                <span>Sales name</span>
                {editing && canEditSalesContact ? (
                  <input
                    value={editFields.sales_staff_name}
                    onChange={(e) => setEditFields({ ...editFields, sales_staff_name: e.target.value })}
                    className="input-shell max-w-[16rem] !py-2 text-sm"
                    placeholder="Salesperson name"
                  />
                ) : (
                  <span className="font-semibold text-slate-900">{call.sales_staff_name || "Unassigned"}</span>
                )}
              </div>
              <div className="flex items-center justify-between gap-4">
                <span>Sales email</span>
                {editing && canEditSalesContact ? (
                  <input
                    type="email"
                    value={editFields.sales_staff_email}
                    onChange={(e) => setEditFields({ ...editFields, sales_staff_email: e.target.value })}
                    className="input-shell max-w-[16rem] !py-2 text-sm"
                    placeholder="sales@example.com"
                  />
                ) : (
                  <span className="font-semibold text-slate-900">{call.sales_staff_email || "Not set"}</span>
                )}
              </div>
              <div className="flex items-center justify-between gap-4">
                <span>Shopper</span>
                {editing ? (
                  <select
                    value={editFields.shopper_id || ""}
                    onChange={(e) =>
                      setEditFields({ ...editFields, shopper_id: e.target.value ? Number(e.target.value) : null })
                    }
                    className="select-shell max-w-[14rem]"
                  >
                    <option value="">Unassigned</option>
                    {shopperList.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                ) : (
                  <span className={`font-semibold ${call.shopper_name ? "text-slate-900" : "text-amber-700"}`}>
                    {call.shopper_name || "Unassigned"}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                {call.is_reshop_call && <span className="badge-pill bg-orange-100 text-orange-800">Reshop</span>}
                {call.is_overdue && <span className="badge-pill bg-rose-100 text-rose-800">Overdue</span>}
                {call.is_override && <span className="badge-pill bg-violet-100 text-violet-800">Override</span>}
                {call.is_survey_completed && <span className="badge-pill bg-emerald-100 text-emerald-800">Survey done</span>}
                {call.is_payment_made && <span className="badge-pill bg-teal-100 text-teal-800">Paid</span>}
              </div>
              {call.percent_achieved != null && (
                <div className="pt-3">
                  <div className="flex items-center justify-between gap-4">
                    <span>Score</span>
                    <span className={`badge-pill ${scoreTone(call.percent_achieved)}`}>{call.percent_achieved}%</span>
                  </div>
                  <div className="progress-track mt-3">
                    <div className="progress-fill" style={{ width: `${Math.min(call.percent_achieved, 100)}%` }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="surface-panel rise-in delay-3 p-6 md:p-7">
            <div className="eyebrow">Audio capture</div>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight">Recording</h2>
            {playableAudioUrl ? (
              <div className="mt-5 surface-muted p-4">
                <audio controls src={playableAudioUrl} className="w-full" />
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                  <span className="badge-pill bg-slate-100 text-slate-700">
                    {call.audio_source === "legacy" ? "Legacy archive" : "Uploaded file"}
                  </span>
                  {call.audio_source === "legacy" && call.legacy_wave_path && (
                    <span className="truncate">Path: {call.legacy_wave_path}</span>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-5 rounded-[1.2rem] border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                No recording is available yet. You can upload one now, or configure
                the backend `LEGACY_RECORDINGS_ROOT` to let migrated calls stream files
                from the legacy archive path.
              </div>
            )}
            <div className="mt-5 space-y-4">
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                className="input-shell"
              />
              <button onClick={handleUploadAudio} disabled={!audioFile} className="button-primary">
                {playableAudioUrl ? "Replace recording" : "Upload recording"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* PDF Report Modal */}
      {showPdfModal && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/50" onClick={closePdfModal}>
          <div className="flex items-center justify-between px-6 py-3 bg-white border-b" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold">Survey Report — Call #{displayCallId}</h3>
            <div className="flex gap-2">
              {pdfUrl && (
                <>
                  <button
                    onClick={() => {
                      const a = document.createElement("a");
                      a.href = pdfUrl;
                      a.download = `SurveyReport_Call_${displayCallId}.pdf`;
                      a.click();
                    }}
                    className="button-ghost text-sm"
                  >
                    Download
                  </button>
                  <button
                    onClick={() => {
                      const w = window.open(pdfUrl);
                      if (w) { w.onload = () => w.print(); }
                    }}
                    className="button-ghost text-sm"
                  >
                    Print
                  </button>
                </>
              )}
              <button onClick={closePdfModal} className="button-ghost text-sm">Close</button>
            </div>
          </div>
          <div className="flex-1" onClick={(e) => e.stopPropagation()}>
            {pdfLoading ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-white text-lg">Generating report...</p>
              </div>
            ) : pdfUrl ? (
              <iframe src={pdfUrl} title="Survey Report" className="w-full h-full" />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-white">Unable to generate report.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit trail */}
      <section className="surface-panel rise-in delay-2 p-6 md:p-7">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="eyebrow">Audit trail</div>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight">History</h2>
          </div>
          <span className="badge-pill bg-slate-900 text-white">{history.length} events</span>
        </div>

        {history.length === 0 ? (
          <div className="empty-state">
            <div>
              <div className="label-caps">No history yet</div>
              <p className="mt-2">Changes to this call will appear here as they happen.</p>
            </div>
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-[1.2rem]">
            <table className="table-modern min-w-full">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Changed By</th>
                  <th>Field</th>
                  <th>Old Value</th>
                  <th>New Value</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry) => (
                  <tr key={entry.id}>
                    <td className="text-sm text-slate-600">{new Date(entry.created_at).toLocaleString()}</td>
                    <td>{entry.changed_by_name || "System"}</td>
                    <td className="font-semibold text-slate-900">{entry.field_changed}</td>
                    <td className="text-sm text-slate-600">{entry.old_value || "-"}</td>
                    <td className="text-sm text-slate-600">{entry.new_value || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Email Review Modal */}
      {showReviewEmail && call && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowReviewEmail(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-100 bg-slate-50 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Email Review Report</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Send survey review and recording to recipients
                  </p>
                </div>
                {emailScore != null && (
                  <span className={`badge-pill text-sm font-bold ${scoreTone(emailScore)}`}>
                    Score: {emailScore}%
                  </span>
                )}
              </div>
              {emailTemplateSource && (
                <p className="text-[10px] text-slate-400 mt-1">Template: {emailTemplateSource}</p>
              )}
            </div>
            <div className="p-5 space-y-4">
              {/* Recipient fields grid */}
              <div>
                <label className="label-caps text-[10px] block mb-2">Recipients</label>
                <div className="grid grid-cols-2 gap-2">
                  {emailRecipientFields.map((rf, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase w-20 shrink-0 text-right">{rf.label}</span>
                      <input
                        value={rf.email}
                        onChange={(e) => {
                          const updated = [...emailRecipientFields];
                          updated[i] = { ...updated[i], email: e.target.value };
                          setEmailRecipientFields(updated);
                        }}
                        className="input-shell text-sm flex-1"
                      />
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setEmailRecipientFields([...emailRecipientFields, { label: "CC", email: "" }])}
                  className="text-xs text-indigo-600 hover:underline mt-2"
                >
                  + Add recipient
                </button>
              </div>
              <div>
                <label className="label-caps text-[10px] block mb-1">Subject</label>
                <input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="input-shell w-full text-sm"
                />
              </div>
              <div>
                <label className="label-caps text-[10px] block mb-1">Body</label>
                <div
                  className="input-shell w-full text-sm min-h-[160px] p-3 prose prose-sm max-w-none overflow-y-auto"
                  contentEditable
                  dangerouslySetInnerHTML={{ __html: emailBody }}
                  onBlur={(e) => setEmailBody(e.currentTarget.innerHTML)}
                />
              </div>
              <div>
                <label className="label-caps text-[10px] block mb-1">Attachments</label>
                <input
                  type="file"
                  multiple
                  onChange={(event) => setEmailAttachments(Array.from(event.target.files || []))}
                  className="input-shell w-full text-sm"
                />
                {emailAttachments.length > 0 && (
                  <div className="mt-2 space-y-1 text-xs text-slate-600">
                    {emailAttachments.map((file) => (
                      <div key={`${file.name}-${file.size}`}>{file.name}</div>
                    ))}
                  </div>
                )}
              </div>
              {/* Links section */}
              <div className="flex gap-4 text-sm">
                {emailReportUrl ? (
                  <a
                    href={emailReportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline font-medium"
                  >
                    Shop Call Review (PDF)
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      openPdfReport();
                    }}
                    className="text-indigo-600 hover:underline font-medium"
                  >
                    Shop Call Review (PDF)
                  </button>
                )}
                {emailWaveUrl && (
                  <a
                    href={emailWaveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline font-medium"
                  >
                    Recorded Call
                  </a>
                )}
              </div>
            </div>
            <div className="p-5 border-t border-slate-100 flex justify-end gap-3">
              <button onClick={() => setShowReviewEmail(false)} className="button-ghost">Cancel</button>
              <button
                onClick={handleSendReviewEmail}
                disabled={emailSending || emailRecipientFields.length === 0}
                className="button-primary"
              >
                {emailSending ? "Sending..." : "Send Review Email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stop Process Email Modal */}
      {showStopEmail && call && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowStopEmail(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-100 bg-rose-50 rounded-t-xl">
              <h3 className="text-lg font-semibold">Stop Process Notification</h3>
              <p className="text-xs text-slate-500 mt-0.5">Send termination / stop process email to sales staff</p>
              {emailTemplateSource && (
                <p className="text-[10px] text-slate-400 mt-1">Template: {emailTemplateSource}</p>
              )}
            </div>
            <div className="p-5 space-y-4">
              {/* Recipient fields grid */}
              <div>
                <label className="label-caps text-[10px] block mb-2">Recipients</label>
                <div className="grid grid-cols-2 gap-2">
                  {emailRecipientFields.map((rf, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase w-20 shrink-0 text-right">{rf.label}</span>
                      <input
                        value={rf.email}
                        onChange={(e) => {
                          const updated = [...emailRecipientFields];
                          updated[i] = { ...updated[i], email: e.target.value };
                          setEmailRecipientFields(updated);
                        }}
                        className="input-shell text-sm flex-1"
                      />
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setEmailRecipientFields([...emailRecipientFields, { label: "CC", email: "" }])}
                  className="text-xs text-indigo-600 hover:underline mt-2"
                >
                  + Add recipient
                </button>
              </div>
              <div>
                <label className="label-caps text-[10px] block mb-1">Subject</label>
                <input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="input-shell w-full text-sm"
                />
              </div>
              <div>
                <label className="label-caps text-[10px] block mb-1">Body</label>
                <div
                  className="input-shell w-full text-sm min-h-[160px] p-3 prose prose-sm max-w-none overflow-y-auto"
                  contentEditable
                  dangerouslySetInnerHTML={{ __html: emailBody }}
                  onBlur={(e) => setEmailBody(e.currentTarget.innerHTML)}
                />
              </div>
            </div>
            <div className="p-5 border-t border-slate-100 flex justify-end gap-3">
              <button onClick={() => setShowStopEmail(false)} className="button-ghost">Cancel</button>
              <button
                onClick={handleSendStopEmail}
                disabled={emailSending || emailRecipientFields.length === 0}
                className="button-primary bg-rose-700 hover:bg-rose-800"
              >
                {emailSending ? "Sending..." : "Send Stop Process Email"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
